import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
  buildAlternates,
  canonicalUrl,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_SIZE,
  type Lang,
  MARKETING_ROUTES,
  OG_LOCALE_MAP,
  resolveMarketingPath,
  splitLangFromPath,
  SUPPORTED_LANGS,
} from '@/seo/marketing-routes';
import { type CopyGetter, landingSubpageCopy, pageJsonLd } from '@/seo/page-content';

import { useMetadata } from './context';
import { SITE_URL } from './site-url';

interface SeoProps {
  /** Key under the `seo.` i18n namespace (e.g. `home`, `flashcard`, `blogPost`). */
  pageKey: string;
  /** Explicit title (dynamic pages). Falls back to `seo.<pageKey>.title`. */
  title?: string;
  /** Explicit description. Falls back to `seo.<pageKey>.description`. */
  description?: string;
  /** Interpolation values passed to i18next for templated keys. */
  values?: Record<string, string>;
  /** Override og:image (absolute or root-relative path). */
  ogImage?: string;
  /** Override the robots directive (e.g. `noindex, follow`). */
  robots?: string;
  /** Comma list or array of keywords. */
  keywords?: string | string[];
  /** Page-specific JSON-LD graphs (FAQ, breadcrumb, article, …). */
  jsonLd?: object[];
}

type SeoSuffix =
  | 'title'
  | 'titleTemplate'
  | 'description'
  | 'descriptionTemplate'
  | 'descriptionFallback'
  | 'ogTitle'
  | 'ogDescription'
  | 'keywords';

/**
 * Every `seo.<pageKey>.<suffix>` key a page can resolve, written out whole. A
 * page or suffix missing here has no copy — the same miss `defaultValue: ''`
 * reports — so adding copy for a page means adding its key here too.
 */
const SEO_PAGE_KEYS: Record<string, Partial<Record<SeoSuffix, string>>> = {
  home: {
    title: 'seo.home.title',
    description: 'seo.home.description',
    ogTitle: 'seo.home.ogTitle',
    ogDescription: 'seo.home.ogDescription',
    keywords: 'seo.home.keywords',
  },
  about: {
    title: 'seo.about.title',
    description: 'seo.about.description',
    ogTitle: 'seo.about.ogTitle',
    ogDescription: 'seo.about.ogDescription',
  },
  upload: {
    title: 'seo.upload.title',
    description: 'seo.upload.description',
    ogTitle: 'seo.upload.ogTitle',
    ogDescription: 'seo.upload.ogDescription',
    keywords: 'seo.upload.keywords',
  },
  flashcard: {
    title: 'seo.flashcard.title',
    description: 'seo.flashcard.description',
    ogTitle: 'seo.flashcard.ogTitle',
    ogDescription: 'seo.flashcard.ogDescription',
    keywords: 'seo.flashcard.keywords',
  },
  highlight: {
    title: 'seo.highlight.title',
    description: 'seo.highlight.description',
    ogTitle: 'seo.highlight.ogTitle',
    ogDescription: 'seo.highlight.ogDescription',
    keywords: 'seo.highlight.keywords',
  },
  quizzes: {
    title: 'seo.quizzes.title',
    description: 'seo.quizzes.description',
    ogTitle: 'seo.quizzes.ogTitle',
    ogDescription: 'seo.quizzes.ogDescription',
    keywords: 'seo.quizzes.keywords',
  },
  study: {
    title: 'seo.study.title',
    description: 'seo.study.description',
    ogTitle: 'seo.study.ogTitle',
    ogDescription: 'seo.study.ogDescription',
    keywords: 'seo.study.keywords',
  },
  mindMaps: {
    title: 'seo.mindMaps.title',
    description: 'seo.mindMaps.description',
  },
  transcriber: {
    title: 'seo.transcriber.title',
    description: 'seo.transcriber.description',
  },
  summaries: {
    title: 'seo.summaries.title',
    description: 'seo.summaries.description',
  },
  imageOcclusion: {
    title: 'seo.imageOcclusion.title',
    description: 'seo.imageOcclusion.description',
  },
  examPlanner: {
    title: 'seo.examPlanner.title',
    description: 'seo.examPlanner.description',
  },
  oralExam: {
    title: 'seo.oralExam.title',
    description: 'seo.oralExam.description',
  },
  caseStudies: {
    title: 'seo.caseStudies.title',
    description: 'seo.caseStudies.description',
  },
  aiAgent: {
    title: 'seo.aiAgent.title',
    description: 'seo.aiAgent.description',
  },
  blog: {
    title: 'seo.blog.title',
    description: 'seo.blog.description',
    ogTitle: 'seo.blog.ogTitle',
    ogDescription: 'seo.blog.ogDescription',
  },
  blogTopics: {
    title: 'seo.blogTopics.title',
    description: 'seo.blogTopics.description',
    ogTitle: 'seo.blogTopics.ogTitle',
    ogDescription: 'seo.blogTopics.ogDescription',
  },
  help: {
    title: 'seo.help.title',
    description: 'seo.help.description',
    ogTitle: 'seo.help.ogTitle',
    ogDescription: 'seo.help.ogDescription',
  },
  terms: {
    title: 'seo.terms.title',
    description: 'seo.terms.description',
    ogTitle: 'seo.terms.ogTitle',
    ogDescription: 'seo.terms.ogDescription',
  },
  legal: {
    title: 'seo.legal.title',
    description: 'seo.legal.description',
    ogTitle: 'seo.legal.ogTitle',
    ogDescription: 'seo.legal.ogDescription',
  },
  privacy: {
    title: 'seo.privacy.title',
    description: 'seo.privacy.description',
    ogTitle: 'seo.privacy.ogTitle',
    ogDescription: 'seo.privacy.ogDescription',
  },
  blogPost: {
    titleTemplate: 'seo.blogPost.titleTemplate',
    descriptionFallback: 'seo.blogPost.descriptionFallback',
  },
  blogTopic: {
    titleTemplate: 'seo.blogTopic.titleTemplate',
    descriptionTemplate: 'seo.blogTopic.descriptionTemplate',
  },
  helpArticle: {
    titleTemplate: 'seo.helpArticle.titleTemplate',
    descriptionFallback: 'seo.helpArticle.descriptionFallback',
  },
  profileOg: {
    titleTemplate: 'seo.profileOg.titleTemplate',
    descriptionTemplate: 'seo.profileOg.descriptionTemplate',
  },
  inviteOg: {
    titleTemplate: 'seo.inviteOg.titleTemplate',
    description: 'seo.inviteOg.description',
  },
};

const toLang = (lng: string | undefined): Lang => {
  const code = (lng ?? 'en').split('-')[0];
  return (SUPPORTED_LANGS as readonly string[]).includes(code) ? (code as Lang) : 'en';
};

/**
 * Declarative SEO for a public page. Resolves localized title/description/OG
 * copy from the `seo` i18n namespace, computes the language-aware canonical URL
 * and full hreflang alternate set, and pushes it all into MetadataContext.
 *
 * Renders nothing — the actual tags are emitted by MetadataRenderer (React 19
 * hoists them into <head>). For non-JS crawlers, the edge middleware injects the
 * same data; this path covers Googlebot + in-app navigation.
 */
export const Seo = ({
  pageKey,
  title: titleOverride,
  description: descriptionOverride,
  values,
  ogImage,
  robots,
  keywords,
  jsonLd,
}: SeoProps) => {
  const { t, i18n } = useTranslation();
  const { setMetadata } = useMetadata();
  const { pathname } = useLocation();

  const lang = toLang(i18n.language);

  // i18next returns the key itself for misses; `defaultValue:''` lets us detect
  // a miss and fall back cleanly.
  const pick = (key: string, fallback: string): string => {
    const value = t(key, { defaultValue: '', ...(values ?? {}) });
    return value || fallback;
  };

  // Resolve a `seo.<pageKey>.<suffix>` value, trying each suffix in order and
  // interpolating `values`. Resolution happens INSIDE <Seo> (with Seo's own `t`)
  // rather than in the page so it stays correct when LangBoundary switches
  // language after first paint — pages that pre-compute translated strings and
  // pass them as props go stale on that transition.
  const pageKeys = Object.hasOwn(SEO_PAGE_KEYS, pageKey) ? SEO_PAGE_KEYS[pageKey] : {};
  const resolve = (suffixes: SeoSuffix[], fallback: string): string => {
    for (const suffix of suffixes) {
      const key = pageKeys[suffix];
      if (!key) continue;
      const value = t(key, {
        defaultValue: '',
        ...(values ?? {}),
      });
      if (value) return value;
    }
    return fallback;
  };

  // Raw copy, as the edge middleware reads it (structured data, feature pages).
  const get: CopyGetter = (key) =>
    i18n.getResource(lang, 'translation', key) ??
    i18n.getResource('en', 'translation', key);

  // One of the landing's own URLs with no `seo.<pageKey>` copy yet: the
  // words it opens on, the same fallback the edge uses.
  const featureCopy = landingSubpageCopy(get, pageKey);

  const siteName = pick('seo.site.name', 'Flashka');
  const title =
    titleOverride ?? resolve(['title', 'titleTemplate'], featureCopy?.title ?? siteName);
  const description =
    descriptionOverride ??
    resolve(
      ['description', 'descriptionTemplate', 'descriptionFallback'],
      featureCopy?.description ?? pick('seo.site.defaultDescription', ''),
    );
  const ogTitle = resolve(['ogTitle'], title);
  const ogDescription = resolve(['ogDescription'], description);

  const keywordList = Array.isArray(keywords)
    ? keywords
    : (keywords ?? (pageKeys.keywords ? pick(pageKeys.keywords, '') : ''))
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);

  // English-equivalent path → drives canonical + hreflang. resolveMarketingPath
  // normalizes `/` → `/home` and validates dynamic shapes.
  const { path: rawEnPath } = splitLangFromPath(pathname);
  const resolved = resolveMarketingPath(rawEnPath);
  const enPath = resolved?.enPath ?? rawEnPath;

  const canonical = canonicalUrl(enPath, lang);
  const alternates = buildAlternates(enPath);
  const ogLocale = OG_LOCALE_MAP[lang];
  const ogLocaleAlternate = SUPPORTED_LANGS.filter((l) => l !== lang).map(
    (l) => OG_LOCALE_MAP[l],
  );

  // The page's own structured data — the SAME graphs the edge middleware puts
  // in the crawler HTML (`seo/page-content`): SoftwareApplication + FAQPage on
  // the landing, AboutPage on About, a plain SoftwareApplication on the feature
  // pages. Read from the raw i18n resources, as the middleware reads the raw
  // locale JSON. Other schemas come via props.
  const route = MARKETING_ROUTES.find((r) => r.path === enPath);
  const autoJsonLd = pageJsonLd({
    seoKey: pageKey,
    route,
    lang,
    get,
    origin: SITE_URL,
    canonical,
    title,
    description,
  });
  const allJsonLd = [...autoJsonLd, ...(jsonLd ?? [])];

  const image = ogImage ?? DEFAULT_OG_IMAGE;
  // Size + alt are known for the default share card only (same rule as the edge).
  const isDefaultImage = image === DEFAULT_OG_IMAGE;
  const imageAlt = isDefaultImage ? pick('seo.site.ogImageAlt', '') : '';

  // Signature drives the effect; mirrors the existing blog-post setMetadata flow.
  const signature = [
    lang,
    pageKey,
    title,
    description,
    enPath,
    robots ?? '',
    image,
    keywordList.join('|'),
    JSON.stringify(allJsonLd),
  ].join('§');

  useEffect(() => {
    setMetadata({
      title,
      description,
      canonical,
      alternates,
      ogLocale,
      ogLocaleAlternate,
      keywords: keywordList.length ? keywordList : undefined,
      ...(robots ? { robots } : {}),
      twitter: { title: ogTitle, description: ogDescription, image },
      openGraph: {
        type: pageKey === 'blogPost' ? 'article' : 'website',
        title: ogTitle,
        description: ogDescription,
        url: canonical,
        images: [image],
        // Always set (even undefined): openGraph merges over the previous page's.
        imageAlt: imageAlt || undefined,
        imageWidth: isDefaultImage ? DEFAULT_OG_IMAGE_SIZE.width : undefined,
        imageHeight: isDefaultImage ? DEFAULT_OG_IMAGE_SIZE.height : undefined,
        siteName: 'Flashka',
      },
      jsonLd: allJsonLd,
    });
  }, [signature]);

  return null;
};
